import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_BASE_URL } from '../../config/api';

interface DashboardSeriesPoint {
  date: string;
  revenue: number;
  revenueByType?: Record<string, number>;
  orders: number;
  newCustomers: number;
  spend: number;
  metaSpend?: number;
  googleSpend?: number;
  cac: number | null;
}

interface DashboardSummary {
  range: { days: number };
  productTypes?: string[];
  selectedTypes?: string[];
  sources: {
    shopify: { configured: boolean; error: string | null };
    meta: { configured: boolean; error: string | null };
    google: { configured: boolean; error: string | null };
  };
  totals: {
    revenue: number;
    orders: number;
    aov: number;
    customers: number;
    newCustomers: number;
    repeatRate: number;
    clv: number;
    spend: number;
    cac: number | null;
    roas: number | null;
  };
  series: DashboardSeriesPoint[];
}

const RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const TYPE_COLORS = ['#2563eb', '#7c3aed', '#db2777', '#0d9488', '#ea580c', '#4f46e5', '#65a30d', '#0369a1'];

const money = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '—';
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const formatDay = (value: string) => {
  const date = new Date(`${value}T00:00:00Z`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const typeColor = (type: string, types: string[]) => TYPE_COLORS[Math.max(0, types.indexOf(type)) % TYPE_COLORS.length];

const StoreDashboardView: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/store-dashboard/summary`, {
        params: {
          days,
          ...(selectedTypes?.length ? { types: selectedTypes.join(',') } : {}),
        },
        withCredentials: true,
      });
      setData(response.data);
      const types = Array.isArray(response.data.productTypes) ? response.data.productTypes : [];
      setProductTypes(types);
      setSelectedTypes((current) => current || types);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [days, selectedTypes]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleType = (type: string) => {
    setSelectedTypes((current) => {
      const selected = current || [];
      return selected.includes(type)
        ? selected.filter((item) => item !== type)
        : [...selected, type];
    });
  };

  const totals = data?.totals;
  const series = data?.series || [];
  const needsApis = data && (!data.sources.shopify.configured || !data.sources.meta.configured || !data.sources.google?.configured);
  const visibleTypes = productTypes.filter((type) => (selectedTypes || productTypes).includes(type));
  const chartSeries = series.map((row) => ({
    ...row,
    ...Object.fromEntries(productTypes.map((type) => [`type:${type}`, row.revenueByType?.[type] || 0])),
  }));

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">Color and Light</div>
          <h1>Sales</h1>
        </div>
        <div className="store-dash-ranges">
          {RANGES.map((range) => (
            <button
              key={range.value}
              type="button"
              className={`store-dash-range${days === range.value ? ' is-active' : ''}`}
              onClick={() => setDays(range.value)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {needsApis && (
        <p className="store-dash-hint">
          Some sources are still off. Connect them on the <Link to="/apis">APIs</Link> page.
        </p>
      )}

      {productTypes.length > 0 && (
        <div className="store-dash-filters">
          <span>Product types</span>
          {productTypes.map((type) => (
            <label key={type} className="store-dash-check">
              <input
                type="checkbox"
                checked={(selectedTypes || productTypes).includes(type)}
                onChange={() => toggleType(type)}
              />
              <i style={{ background: typeColor(type, productTypes) }} />
              {type}
            </label>
          ))}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <div className="store-dash-cards">
            <div className="store-dash-card">
              <span>Revenue</span>
              <strong>{money(totals?.revenue)}</strong>
            </div>
            <div className="store-dash-card">
              <span>AOV</span>
              <strong>{money(totals?.aov)}</strong>
            </div>
            <div className="store-dash-card">
              <span>CLV</span>
              <strong>{money(totals?.clv)}</strong>
            </div>
            <div className="store-dash-card">
              <span>Ad spend</span>
              <strong>{money(totals?.spend)}</strong>
            </div>
            <div className="store-dash-card">
              <span>CAC</span>
              <strong>{money(totals?.cac)}</strong>
            </div>
            <div className="store-dash-card">
              <span>ROAS</span>
              <strong>{totals?.roas == null ? '—' : `${totals.roas.toFixed(2)}x`}</strong>
            </div>
          </div>

          <div className="store-dash-charts">
            <section className="store-dash-chart">
              <h2>Revenue vs ad spend</h2>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={chartSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tickFormatter={formatDay} minTickGap={24} />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip labelFormatter={formatDay} formatter={(value: number) => money(value)} />
                  <Legend />
                  {visibleTypes.map((type) => (
                    <Line
                      key={type}
                      type="monotone"
                      dataKey={`type:${type}`}
                      name={type}
                      stroke={typeColor(type, productTypes)}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                  <Line type="monotone" dataKey="metaSpend" name="Meta spend" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="googleSpend" name="Google spend" stroke="#16a34a" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </section>

            <section className="store-dash-chart">
              <h2>New customers vs CAC</h2>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tickFormatter={formatDay} minTickGap={24} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `$${value}`} />
                  <Tooltip
                    labelFormatter={formatDay}
                    formatter={(value: number, name) => (name === 'CAC' ? money(value) : value)}
                  />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="newCustomers" name="New customers" stroke="#16a34a" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="cac" name="CAC" stroke="#dc2626" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default StoreDashboardView;
